import type {
  ActionCodeStepInput,
  ActionCodeStepOutput,
  ActionCodeStepWrite,
} from 'ts-components/PiChat/protocol';
import type { AutomationVariableDataType } from 'pages/AutomationEngine/types';
import type {
  CodeInput,
  CodeItem,
  CodeOutput,
  CodeStepDetails,
  ConflictResolution,
} from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/CodeStep/types';

export type FieldDisplayName = (id: string) => string;

export const fieldDisplayNameLookup = (
  fields: readonly { id?: string; displayName?: string }[] | null | undefined
): FieldDisplayName => {
  const names = new Map<string, string>();
  for (const field of fields ?? []) {
    if (field?.id) names.set(field.id, field.displayName ?? '');
  }
  return (id) => names.get(id) ?? '';
};

const fieldItem = (
  source: { id: string } | undefined,
  displayName: FieldDisplayName
): CodeItem | null =>
  source ? { id: source.id, displayName: displayName(source.id) } : null;

const staticValueFromWire = (
  staticValue: NonNullable<ActionCodeStepInput['static_value']>
) => {
  if ('employee_id' in staticValue) {
    return {
      value: null,
      customObject: null,
      entityRecord: null,
      employee: staticValue.employee_id
        ? { id: staticValue.employee_id }
        : null,
    };
  }
  if ('custom_object' in staticValue || 'entity_record_id' in staticValue) {
    return {
      value: null,
      customObject: staticValue.custom_object
        ? { id: staticValue.custom_object.id }
        : null,
      entityRecord: staticValue.entity_record_id
        ? { id: staticValue.entity_record_id }
        : null,
      employee: null,
    };
  }
  return {
    value: (staticValue.value ?? null) as string | null,
    customObject: null,
    entityRecord: null,
    employee: null,
  };
};

export const codeInputFromWire = (
  input: ActionCodeStepInput,
  displayName: FieldDisplayName
): CodeInput => {
  const base = {
    name: input.name,
    dataType: input.data_type as AutomationVariableDataType,
    isList: input.is_list,
  };
  switch (input.input_type) {
    case 'variable':
      return {
        ...base,
        inputType: 'variable',
        variable: input.variable ? { name: input.variable.name } : null,
        field: null,
        relatedField: null,
        staticValue: null,
      };
    case 'field':
      return {
        ...base,
        inputType: 'field',
        field: fieldItem(input.field, displayName),
        relatedField: null,
        variable: null,
        staticValue: null,
      };
    case 'related_field':
      return {
        ...base,
        inputType: 'related_field',
        field: fieldItem(input.field, displayName),
        relatedField: fieldItem(input.related_field, displayName),
        variable: null,
        staticValue: null,
      };
    case 'static_value':
      return {
        ...base,
        inputType: 'static_value',
        field: null,
        relatedField: null,
        variable: null,
        staticValue: staticValueFromWire(input.static_value ?? {}),
      };
  }
};

export const codeOutputFromWire = (
  output: ActionCodeStepOutput,
  displayName: FieldDisplayName
): CodeOutput => {
  const base = {
    name: output.name,
    dataType: output.data_type as AutomationVariableDataType,
    isList: output.is_list,
    conflictResolution: output.conflict_resolution as ConflictResolution,
    createMissingOption: output.create_missing_option,
  };
  switch (output.output_type) {
    case 'variable':
      return {
        ...base,
        outputType: 'variable',
        variable: output.variable ? { name: output.variable.name } : null,
        field: null,
        relatedField: null,
      };
    case 'field':
      return {
        ...base,
        outputType: 'field',
        field: fieldItem(output.field, displayName),
        relatedField: null,
        variable: null,
      };
    case 'related_field':
      return {
        ...base,
        outputType: 'related_field',
        field: fieldItem(output.field, displayName),
        relatedField: fieldItem(output.related_field, displayName),
        variable: null,
      };
  }
};

export const codeStepDetailsFromWire = (
  write: ActionCodeStepWrite,
  existing: CodeStepDetails,
  displayName: FieldDisplayName
): CodeStepDetails => ({
  ...existing,
  type: 'code_step',
  hasError: false,
  runtime: write.runtime,
  script: write.script,
  secrets: write.secrets.map((secret) => ({ ...secret })),
  inputs: write.inputs.map((input) => codeInputFromWire(input, displayName)),
  outputs: write.outputs.map((output) =>
    codeOutputFromWire(output, displayName)
  ),
});
